"use client";

import { Box } from "@mui/material";
import { TOP_BAR_HEIGHT } from "@v2/components/header/constants";
import { ArrowDown } from "lucide-react";
import { MouseEvent, useCallback } from "react";

// The site header hides itself once the page has scrolled this far down.
const HEADER_HIDE_THRESHOLD = 116;

// The dark why-vahdat section needs the inverse fill; everywhere else sits on white.
export default function PlpScrollToProductsButton({ label, targetId, light = false }: { label: string; targetId: string; light?: boolean }) {
    const onClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
        const target = document.getElementById(targetId);
        if (!target) return;

        event.preventDefault();

        const stickyHeader = document.getElementById("plp_header");
        const targetTop = target.getBoundingClientRect().top + window.scrollY;

        // Scrolling down hides the site header, which lifts the sticky plp header up
        // to the top bar. The landing spot has to use that resting position, not the
        // lower one measured while the header is still open, or the list stops short.
        const restingOffset = stickyHeader
            ? TOP_BAR_HEIGHT + stickyHeader.offsetHeight
            : Number.parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
        const openOffset = stickyHeader
            ? (Number.parseFloat(window.getComputedStyle(stickyHeader).top) || 0) + stickyHeader.offsetHeight
            : restingOffset;

        const restingTop = targetTop - restingOffset;
        // A hop this short never travels far enough for the header to hide.
        const top = restingTop >= HEADER_HIDE_THRESHOLD ? restingTop : targetTop - openOffset;

        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, [targetId]);

    return (
        <Box
            component="a"
            href={`#${targetId}`}
            onClick={onClick}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                // The column is capped at 560, so a full width button only reads as full
                // width while the viewport is narrower than that. Past sm it would just
                // be a 560px slab, so it falls back to its own width there.
                width: { xs: "100%", sm: "auto" },
                bgcolor: light ? "#fff" : "grey.900",
                color: light ? "grey.900" : "#fff",
                px: 3,
                py: 1.6,
                borderRadius: 1,
                fontSize: 14,
                fontWeight: 700,
                textDecoration: "none",
                transition: "150ms",
                "&:hover": { bgcolor: light ? "grey.200" : "grey.800" },
            }}
        >
            {label}
            <ArrowDown size={16} strokeWidth={2.4} color={light ? "#212121" : "#fff"} />
        </Box>
    );
}
